import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Loader2 } from "lucide-react";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import NewsletterBox from "@/components/NewsletterBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["Church Service", "Festival", "Tour / Concert", "Private Event", "Feature / Collaboration", "General Inquiry"];

const inputCls =
    "w-full bg-black/60 border border-amber-500/25 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-amber-400/70 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200";

const Label = ({ children }) => (
    <label className="block font-meta text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2">{children}</label>
);

const INITIAL = { name: "", email: "", event_type: EVENT_TYPES[0], date: "", location: "", budget: "", message: "" };

const BookingForm = () => {
    const [form, setForm] = useState(INITIAL);
    const [loading, setLoading] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/contact`, {
                ...form,
                date: form.date || null,
                location: form.location || null,
                budget: form.budget || null,
            });
            toast.success(data.message || "Inquiry sent. We'll be in touch soon.");
            setForm(INITIAL);
        } catch (err) {
            toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="booking-form"
            className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-b from-zinc-900/80 to-black/80 backdrop-blur-md p-7 sm:p-9"
        >
            <div className="absolute -top-20 -left-20 w-56 h-56 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
            <div className="flex items-center gap-2 text-amber-300">
                <CalendarCheck size={18} />
                <p className="font-meta text-[11px] tracking-[0.3em] uppercase">Booking & Inquiries</p>
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-white mt-3">
                Bring the Kingdom sound to your stage.
            </h3>

            <div className="grid sm:grid-cols-2 gap-5 mt-7">
                <div>
                    <Label>Your Name *</Label>
                    <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="booking-form-name-input" className={inputCls} />
                </div>
                <div>
                    <Label>Email *</Label>
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="booking-form-email-input" className={inputCls} />
                </div>
                <div>
                    <Label>Event Type</Label>
                    <select value={form.event_type} onChange={set("event_type")} data-testid="booking-form-event-type-select" className={`${inputCls} appearance-none`}>
                        {EVENT_TYPES.map((t) => (
                            <option key={t} value={t} className="bg-zinc-900">
                                {t}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label>Event Date</Label>
                    <input type="date" value={form.date} onChange={set("date")} data-testid="booking-form-date-input" className={`${inputCls} [color-scheme:dark]`} />
                </div>
                <div>
                    <Label>Location</Label>
                    <input value={form.location} onChange={set("location")} placeholder="City, venue" data-testid="booking-form-location-input" className={inputCls} />
                </div>
                <div>
                    <Label>Budget Range</Label>
                    <input value={form.budget} onChange={set("budget")} placeholder="e.g. $1,500 – $3,000" data-testid="booking-form-budget-input" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                    <Label>Message *</Label>
                    <textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell us about your event, audience, and vision…"
                        data-testid="booking-form-message-input"
                        className={`${inputCls} resize-none`}
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={loading}
                data-testid="booking-form-submit-button"
                className="mt-7 inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600 text-black font-bold text-sm px-8 py-3.5 rounded-full hover:shadow-[0_0_25px_rgba(200,151,43,0.4)] hover:scale-[1.03] transition-all duration-200 disabled:opacity-60 disabled:hover:scale-100"
            >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending…" : "Send Inquiry"}
            </button>
            <p className="mt-4 font-meta text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                We read every message. Expect a reply soon.
            </p>
        </form>
    );
};

const Contact = () => (
    <div data-testid="contact-page" className="pt-16 sm:pt-[72px]">
        <section className="relative px-5 sm:px-8 pt-20 pb-12 overflow-hidden">
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] bg-amber-500/10 blur-[130px] rounded-full pointer-events-none" />
            <div className="max-w-7xl mx-auto">
                <SectionHeading eyebrow="Connect" title="Let's Build Something Eternal" />
                <FadeIn delay={0.1}>
                    <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">
                        Bookings, features, ministry partnerships, or just to say the music moved you — the door is open.
                    </p>
                </FadeIn>
            </div>
        </section>

        <section className="px-5 sm:px-8 pb-24">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-6 items-start">
                <FadeIn className="lg:col-span-3">
                    <BookingForm />
                </FadeIn>
                <FadeIn delay={0.15} className="lg:col-span-2">
                    <NewsletterBox />
                </FadeIn>
            </div>
        </section>
    </div>
);

export default Contact;
